import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { TenantApiError } from "@qr-menu/api-tenant";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
} from "@qr-menu/ui";
import { tenantApi } from "../lib/api";
import { writeTenantToken } from "../lib/session";

interface LoginLocationState {
  message?: string;
}

export function LoginRoute() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // A one-off message handed in via router state — today only the
  // "parolanız güncellendi" notice after a successful reset-password
  // redirect. Read once into local state rather than on every render of
  // location.state, and immediately clear the history entry's own state so
  // a later refresh of /login does not resurrect it.
  const [noticeMessage] = useState<string | null>(() => {
    const state = location.state as LoginLocationState | null;
    return state?.message ?? null;
  });
  const clearedNoticeRef = useRef(false);
  useEffect(() => {
    if (clearedNoticeRef.current || !noticeMessage) return;
    clearedNoticeRef.current = true;
    navigate(".", { replace: true, state: null });
  }, [noticeMessage, navigate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError("E-posta ve parola zorunludur.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await tenantApi.login({
        email: trimmedEmail,
        password,
      });
      writeTenantToken(res.access_token);
      setPassword("");
      navigate("/app", { replace: true });
    } catch (err) {
      const message =
        err instanceof TenantApiError && err.status === 401
          ? "E-posta veya parola hatalı."
          : "Giriş yapılamadı.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell">
      <Card className="auth-card">
        <CardHeader>
          <CardTitle>Tenant oturumu</CardTitle>
          <CardDescription>
            Hesap bilgilerinizle giriş yapın. Oturum yalnızca bellekte
            tutulur; sayfayı yenilemek oturumu sonlandırır.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {noticeMessage && <div className="state ok">{noticeMessage}</div>}
          <form className="login-form" onSubmit={handleSubmit}>
            <Field label="E-posta" htmlFor="login-email">
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                spellCheck={false}
                required
              />
            </Field>
            <Field label="Parola" htmlFor="login-password">
              <input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                spellCheck={false}
                required
              />
            </Field>
            <p className="hint">
              <Link to="/forgot-password">Parolamı unuttum</Link>
            </p>
            {error && <div className="state error">{error}</div>}
            <div className="login-actions">
              <button type="submit" className="primary" disabled={submitting}>
                {submitting ? "Gönderiliyor…" : "Oturumu başlat"}
              </button>
            </div>
          </form>
          <p className="hint">
            Hesabınız yok mu? <Link to="/register">Kayıt olun</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
